import { useEffect, useState } from 'react';
import { Bell } from '@phosphor-icons/react';
import { useNotifPrefs, useUpdateNotifPrefs } from '@/hooks/useNotifPrefs';
import type { NotifPref } from '@/types';

// ============================================================
// NotificacionesPanel - preferencias de notificaciones de Teams del usuario
// actual (self-service). Switches agrupados: "Mías" (por rol), "Observador"
// (todas las tareas) y "Otros". Se guarda al toggear (optimista vía estado
// local + PUT). Default: mías ON, observador OFF.
//
// Se monta tanto en el modal de la campana (TopBar) como en cualquier otro
// lado que lo necesite. La cabecera (titulo + subtitulo) es opcional: el modal
// la pone afuera, asi que aca se puede ocultar con showHeader={false}.
// ============================================================
const GRUPO_MIAS: { key: keyof NotifPref; label: string; desc: string }[] = [
  { key: 'notifAsignada', label: 'Me asignan una tarea', desc: 'Cuando me ponen como responsable.' },
  { key: 'notifAsignadaRevisor', label: 'Me asignan como revisor', desc: 'Cuando me ponen como Autorizado Interno.' },
  { key: 'notifRevisionRevisor', label: 'Tarea que reviso pasó a Revisión', desc: 'Una tarea donde soy revisor está lista para que la revise.' },
  { key: 'notifMiCompletada', label: 'Mi tarea se completó', desc: 'Una tarea mía pasó a Completada.' },
  { key: 'notifMiCorreccion', label: 'Mi tarea necesita correcciones', desc: 'Una tarea mía volvió a Correcciones.' },
  { key: 'notifMiEnProceso', label: 'Mi tarea pasó a En Proceso', desc: 'Una tarea mía empezó a trabajarse.' },
  { key: 'notifMiEliminada', label: 'Se eliminó una tarea mía', desc: 'Borraron una tarea donde era responsable.' },
];
const GRUPO_TODAS: { key: keyof NotifPref; label: string; desc: string }[] = [
  { key: 'notifTodasCreadas', label: 'Cuando se crea cualquier tarea', desc: 'Avisarme de toda tarea nueva (vista de supervisor).' },
  { key: 'notifTodasEnProceso', label: 'Cuando cualquier tarea pasa a En Proceso', desc: 'Avisarme cada vez que una tarea empieza.' },
  { key: 'notifTodasEliminadas', label: 'Cuando se elimina cualquier tarea', desc: 'Avisarme de toda eliminación.' },
];
const GRUPO_OTROS: { key: keyof NotifPref; label: string; desc: string }[] = [
  { key: 'notifRecordatorioDiario', label: 'Recordatorio diario', desc: 'El aviso de “Recuerda revisar ArquiFlow” (7:30 y 16:30).' },
];

export function NotificacionesPanel({ showHeader = true }: { showHeader?: boolean }) {
  const { data, isLoading } = useNotifPrefs();
  const update = useUpdateNotifPrefs();
  const [local, setLocal] = useState<NotifPref | null>(null);

  useEffect(() => { if (data) setLocal(data); }, [data]);

  if (isLoading || !local) {
    return <div className="text-sm text-stone-500 italic">Cargando…</div>;
  }

  function toggle(key: keyof NotifPref) {
    if (!local) return;
    const next = { ...local, [key]: !local[key] };
    setLocal(next);            // optimista
    update.mutate(next);
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h2 className="text-base font-semibold text-stone-900 flex items-center gap-2">
            <Bell size={16} weight="bold" />
            Mis notificaciones de Teams
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Elegí qué avisos te llegan al Teams. Los cambios se guardan solos.
          </p>
        </div>
      )}

      <NotifGroup title="Sobre mis tareas" subtitle="Cuando soy responsable o revisor"
        items={GRUPO_MIAS} prefs={local} onToggle={toggle} />
      <NotifGroup title="Observador (todas las tareas)" subtitle="Opt-in: avisos de cualquier tarea, sin importar mi rol"
        items={GRUPO_TODAS} prefs={local} onToggle={toggle} />
      <NotifGroup title="Otros" subtitle=""
        items={GRUPO_OTROS} prefs={local} onToggle={toggle} />
    </div>
  );
}

function NotifGroup({
  title, subtitle, items, prefs, onToggle,
}: {
  title: string;
  subtitle: string;
  items: { key: keyof NotifPref; label: string; desc: string }[];
  prefs: NotifPref;
  onToggle: (key: keyof NotifPref) => void;
}) {
  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50">
        <h3 className="text-sm font-semibold text-stone-700">{title}</h3>
        {subtitle && <p className="text-[11px] text-stone-500">{subtitle}</p>}
      </div>
      <ul className="divide-y divide-stone-100">
        {items.map((it) => (
          <li key={it.key} className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm text-stone-800">{it.label}</p>
              <p className="text-[11px] text-stone-500">{it.desc}</p>
            </div>
            <NotifSwitch checked={prefs[it.key]} onChange={() => onToggle(it.key)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function NotifSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-100 ${
        checked ? 'bg-brand-green-100' : 'bg-stone-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
